<template>
    <div>
      <SettingHeaderTab />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Inventory Subcategories</h5>
          <p class="page-sub">Organise your products with subcategories under each category</p>
        </div>
        <div class="page-actions">
  
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input
              type="text"
              class="search-input"
              placeholder="Search subcategories..."
              v-model="query"
            />
          </div>
  
          <button
            class="btn-danger-outline"
            :disabled="!canBulkDelete"
            data-bs-toggle="modal"
            data-bs-target="#bulkModal"
          >
            <font-awesome-icon icon="trash" />
            Bulk Delete
            <span v-if="selectedModel.length > 0" class="bulk-count">{{ selectedModel.length }}</span>
          </button>
  
          <button
            class="btn-primary-action"
            :disabled="permission.create_inventory_category != 1"
            @click="openCreate"
          >
            <font-awesome-icon icon="plus" /> Create Subcategory
          </button>
  
        </div>
      </div>
  
      <!-- Data card -->
      <div class="data-card">
  
        <!-- Loading -->
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted-cell">Loading subcategories...</p>
        </div>
  
        <!-- 403 -->
        <div v-else-if="permission_status === 403" class="state-block">
          <div class="state-icon state-icon--red"><font-awesome-icon icon="lock" /></div>
          <p class="state-title">Access Denied</p>
          <p class="state-sub">You don't have permission to view subcategories.</p>
        </div>
  
        <!-- Empty -->
        <div v-else-if="subcategories.length === 0" class="state-block">
          <div class="state-icon state-icon--blue"><font-awesome-icon icon="layer-group" /></div>
          <p class="state-title">No subcategories found</p>
          <p class="state-sub">
            {{ query ? 'No results match your search.' : 'Create your first subcategory to get started.' }}
          </p>
          <button
            v-if="!query && permission.create_inventory_category == 1"
            class="btn-primary-action mt-3"
            @click="openCreate"
          >
            <font-awesome-icon icon="plus" /> Create Subcategory
          </button>
        </div>
  
        <template v-else>
  
          <!-- Toolbar -->
          <div class="table-toolbar">
            <span class="table-count">
              <strong>{{ subcategories.length }}</strong> subcategor{{ subcategories.length !== 1 ? 'ies' : 'y' }}
              <span v-if="selectedModel.length > 0" class="selected-hint">· {{ selectedModel.length }} selected</span>
            </span>
          </div>
  
          <!-- Table -->
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th class="col-check">
                    <input type="checkbox" class="row-check" v-model="allSelected" @change="selectAll" />
                  </th>
                  <th>Subcategory Name</th>
                  <th>Parent Category</th>
                  <th class="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="sub in subcategories"
                  :key="sub.id"
                  :class="{ 'row--selected': selectedModel.includes(sub.id) }"
                >
                  <td class="col-check">
                    <input type="checkbox" class="row-check" v-model="selectedModel" :value="sub.id" />
                  </td>
  
                  <td>
                    <div class="sub-cell">
                      <div class="sub-icon"><font-awesome-icon icon="layer-group" /></div>
                      <span class="sub-name">{{ sub.name }}</span>
                    </div>
                  </td>
  
                  <td>
                    <span v-if="sub.category?.category_name" class="category-tag">
                      <font-awesome-icon icon="tag" class="me-1" />
                      {{ sub.category.category_name }}
                    </span>
                    <span v-else class="text-muted-cell">—</span>
                  </td>
  
                  <td class="col-actions">
                    <div class="actions-cell">
                      <button
                        class="action-btn action-btn--edit"
                        :disabled="permission.edit_inventory_category != 1"
                        title="Edit"
                        @click="openEdit(sub)"
                      >
                        <font-awesome-icon icon="pen" />
                      </button>
                      <button
                        class="action-btn action-btn--delete"
                        :disabled="permission.delete_inventory_category != 1"
                        title="Delete"
                        @click="openDelete(sub)"
                      >
                        <font-awesome-icon icon="trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
  
        </template>
      </div>
  
      <!-- ── Create Modal ── -->
      <div class="modal-overlay" :class="{ open: createOpen }" @click.self="closeCreate">
        <div class="modal-box">
          <div class="modal-header-styled">
            <span class="modal-title-styled">Create Subcategory</span>
            <button class="modal-close-btn" @click="closeCreate"><font-awesome-icon icon="times" /></button>
          </div>
          <form @submit.prevent="createSubcategory">
            <div class="modal-body-styled">
              <div class="form-group-styled">
                <label class="form-label-styled">Parent Category <span class="req">*</span></label>
                <Multiselect
                  v-model="category_id"
                  label="name"
                  :options="categories"
                  placeholder="Select a category..."
                  :searchable="true"
                  :create-option="false"
                  :close-on-select="true"
                />
              </div>
              <div class="form-group-styled" style="margin-bottom:0;">
                <label class="form-label-styled">Subcategory Name <span class="req">*</span></label>
                <input
                  ref="createInput"
                  v-model="name"
                  type="text"
                  class="form-input-styled"
                  placeholder="e.g. Smartphones, T-shirts..."
                  required
                />
              </div>
            </div>
            <div class="modal-footer-styled">
              <button type="button" class="btn-ghost" @click="closeCreate">Cancel</button>
              <button type="submit" class="btn-primary-action" :disabled="!canSubmit || loading">
                <font-awesome-icon v-if="loading" icon="spinner" spin class="me-1" />
                {{ loading ? 'Creating...' : 'Create Subcategory' }}
              </button>
            </div>
          </form>
        </div>
      </div>
  
      <!-- ── Edit Modal ── -->
      <div class="modal-overlay" :class="{ open: editOpen }" @click.self="closeEdit">
        <div class="modal-box">
          <div class="modal-header-styled">
            <span class="modal-title-styled">Edit Subcategory</span>
            <button class="modal-close-btn" @click="closeEdit"><font-awesome-icon icon="times" /></button>
          </div>
          <form @submit.prevent="updateSubcategory">
            <div class="modal-body-styled">
              <div class="form-group-styled">
                <label class="form-label-styled">Parent Category <span class="req">*</span></label>
                <Multiselect
                  v-model="category_id"
                  label="name"
                  :options="categories"
                  placeholder="Select a category..."
                  :searchable="true"
                  :create-option="false"
                  :close-on-select="true"
                />
              </div>
              <div class="form-group-styled" style="margin-bottom:0;">
                <label class="form-label-styled">Subcategory Name <span class="req">*</span></label>
                <input
                  v-model="name"
                  type="text"
                  class="form-input-styled"
                  placeholder="Enter subcategory name"
                  required
                />
              </div>
            </div>
            <div class="modal-footer-styled">
              <button type="button" class="btn-ghost" @click="closeEdit">Cancel</button>
              <button type="submit" class="btn-primary-action" :disabled="!canSubmit || loading">
                <font-awesome-icon v-if="loading" icon="spinner" spin class="me-1" />
                {{ loading ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
          </form>
        </div>
      </div>
  
      <!-- Backdrop -->
      <div v-if="createOpen || editOpen" class="modal-backdrop-custom" />
  
      <!-- Delete modals -->
      <SingleDeleteModal
        :modalId="'singleModal'"
        @deleteSingle="callSingleSubcategoryDeleteButton"
        :modalText="'Are you sure you want to delete this subcategory?'"
        :action="'deleteSingle'"
      />
      <BulkDeleteModal
        :modalId="'bulkModal'"
        @deleteBulk="callBulkSubcategoryDeleteButton"
        :modalText="'Are you sure you want to delete the selected subcategories?'"
      />
  
    </div>
  </template>
  
  <script>
  import { ref, computed, watch, nextTick, onMounted } from 'vue';
  import { useStore } from 'vuex';
  import { useToast } from 'vue-toastification';
  import axios from 'axios';
  import Multiselect from '@vueform/multiselect';
  import SettingHeaderTab from './components/settingheadertab.vue';
  import SingleDeleteModal from '../../../components/ActionModal.vue';
  import BulkDeleteModal   from '../../../components/bulkDeleteModal.vue';
  
  export default {
    name: 'InventorySubcategory',
    components: { SettingHeaderTab, SingleDeleteModal, BulkDeleteModal, Multiselect },
  
    setup() {
      const store = useStore();
      const toast = useToast();
  
      // ── State ────────────────────────────────────────────────────────
      const subcategories   = ref([]);
      const categories      = ref([]);
      const loading         = ref(false);
      const permission_status = ref(null);
      const query           = ref('');
      const name            = ref('');
      const category_id     = ref('');
      const subcategory_id  = ref('');
      const selectedModel   = ref([]);
      const allSelected     = ref(false);
      const createOpen      = ref(false);
      const editOpen        = ref(false);
      const createInput     = ref(null);
      const validationErrors = ref([]);
  
      const permission   = computed(() => store.state.getCurrentUserPermission);
      const canBulkDelete = computed(() =>
        permission.value.delete_inventory_category == 1 && selectedModel.value.length > 0,
      );
      const canSubmit = computed(() => name.value.trim() && category_id.value);
  
      // ── Selection ────────────────────────────────────────────────────
      const selectAll = () => {
        selectedModel.value = allSelected.value ? subcategories.value.map(s => s.id) : [];
      };
  
      // ── Modal helpers ────────────────────────────────────────────────
      const resetFields = () => {
        name.value         = '';
        category_id.value  = '';
        subcategory_id.value = '';
        validationErrors.value = [];
      };
  
      const openCreate = async () => {
        resetFields();
        createOpen.value = true;
        await nextTick();
        createInput.value?.focus();
      };
  
      const closeCreate = () => { createOpen.value = false; resetFields(); };
  
      const openEdit = (sub) => {
        subcategory_id.value = sub.id;
        name.value           = sub.name;
        category_id.value    = sub.category_id;
        editOpen.value       = true;
      };
  
      const closeEdit = () => { editOpen.value = false; resetFields(); };
  
      const openDelete = (sub) => {
        subcategory_id.value = sub.id;
        window.$('#singleModal').modal('show');
      };
  
      // ── Data ─────────────────────────────────────────────────────────
      const getAllSubcategories = async () => {
        loading.value = true;
        try {
          const { data } = await axios.get('settings/subcategory', { params: { query: query.value } });
          subcategories.value = data.data;
        } catch (err) {
          permission_status.value = err?.response?.status;
          toast.error(err.response?.data?.message ?? 'Could not load subcategories.');
        } finally {
          loading.value = false;
        }
      };
  
      const getAllCategories = async () => {
        try {
          const { data } = await axios.get('settings/category/all');
          categories.value = data.data.map(c => ({ value: c.id, name: c.category_name }));
        } catch (err) {
            console.log(err?.response?.data?.message);
        }
      };
  
      // ── CRUD ─────────────────────────────────────────────────────────
      const createSubcategory = async () => {
        if (!canSubmit.value) return;
        loading.value = true;
        try {
          await axios.post('/settings/subcategory', { category_id: category_id.value, name: name.value });
          toast.success('Subcategory created successfully.');
          closeCreate();
          getAllSubcategories();
        } catch (err) {
          handleError(err);
        } finally {
          loading.value = false;
        }
      };
  
      const updateSubcategory = async () => {
        if (!canSubmit.value) return;
        loading.value = true;
        try {
          await axios.put(`/settings/subcategory/${subcategory_id.value}`, { category_id: category_id.value, name: name.value });
          toast.success('Subcategory updated successfully.');
          closeEdit();
          getAllSubcategories();
        } catch (err) {
          handleError(err);
        } finally {
          loading.value = false;
        }
      };
  
      const callSingleSubcategoryDeleteButton = async () => {
        try {
          await axios.delete(`/settings/subcategory/${subcategory_id.value}`);
          toast.success('Subcategory deleted successfully.');
          resetFields();
          getAllSubcategories();
        } catch (err) {
          toast.error(err.response?.data?.message ?? 'Could not delete subcategory.');
        }
      };
  
      const callBulkSubcategoryDeleteButton = async () => {
        try {
          const { data } = await axios.post('/settings/subcategory/delete-bulk', { subcategories: selectedModel.value });
          toast.success(data.message);
          selectedModel.value = [];
          allSelected.value   = false;
          getAllSubcategories();
        } catch (err) {
          const status = err.response?.status;
          toast.error(status === 422 ? err.response.data.message[0] : err.response?.data?.message ?? 'Bulk delete failed.');
        }
      };
  
      const handleError = (err) => {
        const status = err.response?.status;
        if (status === 422) {
          toast.warning(err.response.data.message[0]);
          validationErrors.value = err.response.data.message;
        } else if (status === 403) {
          permission_status.value = 403;
          toast.error(err.response.data.message);
        } else {
          toast.error('An error occurred. Please try again.');
        }
      };
  
      watch(query, getAllSubcategories);
  
      onMounted(() => {
        getAllSubcategories();
        getAllCategories();
      });
  
      return {
        subcategories, categories, loading, permission_status, query,
        name, category_id, subcategory_id, validationErrors,
        selectedModel, allSelected, selectAll,
        createOpen, editOpen, createInput,
        openCreate, closeCreate, openEdit, closeEdit, openDelete,
        createSubcategory, updateSubcategory,
        callSingleSubcategoryDeleteButton, callBulkSubcategoryDeleteButton,
        permission, canBulkDelete, canSubmit,
      };
    },
  };
  </script>
  
  <style scoped>
  /* ── Page ────────────────────────────────────────────────────────────── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  
  /* ── Search ──────────────────────────────────────────────────────────── */
  .search-wrap  { position:relative; display:flex; align-items:center; }
  .search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .search-input { padding:8px 12px 8px 33px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:210px; outline:none; transition:border-color .15s; }
  .search-input:focus { border-color:#004AAD; }
  
  /* ── Buttons ─────────────────────────────────────────────────────────── */
  .btn-primary-action  { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-primary-action:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-action:disabled { opacity:.45; cursor:not-allowed; }
  .btn-danger-outline  { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#e03535; border:1px solid #fecaca; font-size:13px; font-weight:600; padding:8px 14px; border-radius:8px; cursor:pointer; transition:all .15s; white-space:nowrap; }
  .btn-danger-outline:hover:not(:disabled) { background:#fff5f5; }
  .btn-danger-outline:disabled { opacity:.45; cursor:not-allowed; }
  .bulk-count  { display:inline-flex; align-items:center; justify-content:center; background:#e03535; color:#fff; border-radius:10px; font-size:11px; font-weight:700; padding:1px 6px; }
  .btn-ghost   { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
  .btn-ghost:hover { background:#e4eaf2; }
  .mt-3 { margin-top:12px; }
  .me-1 { margin-right:4px; }
  
  /* ── Data card ───────────────────────────────────────────────────────── */
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  /* ── States ──────────────────────────────────────────────────────────── */
  .state-block      { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon       { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--blue { background:rgba(0,74,173,.08); color:#004AAD; }
  .state-icon--red  { background:#fff0f0; color:#e03535; }
  .state-title      { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub        { font-size:13px; color:#7a90b0; margin:0; }
  .text-muted-cell  { color:#4a5e78; }
  
  /* ── Table toolbar ───────────────────────────────────────────────────── */
  .table-toolbar  { display:flex; align-items:center; padding:14px 20px; border-bottom:1px solid #e4eaf2; }
  .table-count    { font-size:13px; color:#7a90b0; }
  .table-count strong { color:#1a2b45; }
  .selected-hint  { color:#004AAD; font-weight:600; }
  
  /* ── Table ───────────────────────────────────────────────────────────── */
  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody tr.row--selected { background:#edf4ff; }
  .data-table tbody td { padding:12px 16px; font-size:13px; vertical-align:middle; }
  .col-check   { width:44px; padding-left:20px !important; }
  .col-actions { width:90px; text-align:center; }
  .row-check   { width:15px; height:15px; cursor:pointer; accent-color:#004AAD; }
  
  /* ── Subcategory cell ────────────────────────────────────────────────── */
  .sub-cell { display:flex; align-items:center; gap:10px; }
  .sub-icon { width:30px; height:30px; border-radius:7px; background:#eef2ff; color:#4f46e5; display:flex; align-items:center; justify-content:center; font-size:12px; flex-shrink:0; }
  .sub-name { font-size:13px; font-weight:500; color:#1a2b45; }
  
  /* ── Category tag ────────────────────────────────────────────────────── */
  .category-tag { display:inline-flex; align-items:center; background:rgba(0,74,173,.08); color:#004AAD; border:1px solid rgba(0,74,173,.18); border-radius:5px; padding:3px 9px; font-size:12px; font-weight:600; }
  
  /* ── Action buttons ──────────────────────────────────────────────────── */
  .actions-cell      { display:flex; align-items:center; justify-content:center; gap:6px; }
  .action-btn        { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:#fff; display:flex; align-items:center; justify-content:center; font-size:12px; color:#7a90b0; cursor:pointer; transition:all .15s; }
  .action-btn--edit:hover:not(:disabled)   { background:#e8f4fd; color:#004AAD; border-color:#c5d6f5; }
  .action-btn--delete:hover:not(:disabled) { background:#fff0f0; color:#e03535; border-color:#f5c6c6; }
  .action-btn:disabled { opacity:.35; pointer-events:none; }
  
  /* ── Modal ───────────────────────────────────────────────────────────── */
  .modal-overlay         { position:fixed; inset:0; z-index:1050; display:flex; align-items:center; justify-content:center; opacity:0; pointer-events:none; transition:opacity .2s; }
  .modal-overlay.open    { opacity:1; pointer-events:all; }
  .modal-backdrop-custom { position:fixed; inset:0; z-index:1040; background:rgba(15,25,50,.45); backdrop-filter:blur(2px); }
  .modal-box             { background:#fff; border-radius:14px; width:100%; max-width:440px; margin:16px; box-shadow:0 20px 60px rgba(0,0,0,.18); overflow:hidden; transform:translateY(16px); transition:transform .22s ease; z-index:1051; position:relative; }
  .modal-overlay.open .modal-box { transform:translateY(0); }
  .modal-header-styled   { padding:20px 24px 16px; border-bottom:1px solid #e4eaf2; display:flex; align-items:center; justify-content:space-between; }
  .modal-title-styled    { font-size:16px; font-weight:700; color:#1a2b45; }
  .modal-close-btn       { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:transparent; cursor:pointer; display:flex; align-items:center; justify-content:center; color:#7a90b0; font-size:13px; transition:background .15s; }
  .modal-close-btn:hover { background:#f4f7fb; }
  .modal-body-styled     { padding:20px 24px; display:flex; flex-direction:column; gap:16px; }
  .modal-footer-styled   { padding:16px 24px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; gap:10px; }
  
  /* ── Form ────────────────────────────────────────────────────────────── */
  .form-group-styled  { display:flex; flex-direction:column; gap:6px; }
  .form-label-styled  { font-size:13px; font-weight:600; color:#1a2b45; }
  .req                { color:#e03535; }
  .form-input-styled  { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; transition:border-color .15s, box-shadow .15s; font-family:inherit; }
  .form-input-styled:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  
  /* ── Responsive ──────────────────────────────────────────────────────── */
  @media (max-width:768px) {
    .page-header, .page-actions { flex-direction:column; align-items:stretch; }
    .search-input { width:100%; }
  }
  </style>